import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {LoginService} from "../login/login.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {AuthResponseData} from "../login/auth-response-data.model";
import {TranslationService} from "../../services/translation.service";
import { isPlatformBrowser } from "@angular/common";
import {SsoConfiguration} from "../login/sso-configuration";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {HttpParams} from "@angular/common/http";
import {LoadingSpinnerComponent} from "../loading-spinner/loading-spinner.component";
import {MatButton} from "@angular/material/button";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {NavigationService} from '../../../data/services/navigation.service';
import {ApplyIfNotEmptyDirective} from "../../directives/apply-if-not-empty.directive";

@Component({
  selector: 'app-login-formular',
  templateUrl: './login-formular.component.html',
  styleUrls: ['./login-formular.component.scss'],
  imports: [
    LoadingSpinnerComponent,
    MatButton,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatError,
    ApplyIfNotEmptyDirective
  ],
  standalone: true
})
export class LoginFormularComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private sanitizer = inject(DomSanitizer);
  translationService = inject(TranslationService);
  private navigationService = inject(NavigationService);
  private platformId = inject<Object>(PLATFORM_ID);

  loginForm: FormGroup;
  loading: boolean = false;
  redirecting: boolean = false;
  error: string;
  returnUrl: string = null;
  isAuthenticated: boolean = false;
  authSub: Subscription;
  authData: AuthResponseData = null;

  ssoConfiguration: SsoConfiguration;
  useHassoForm = false;
  hassoLoginUrl: SafeResourceUrl = null;
  prefillUsername = '';
  assignClientNo = 0;
  public ssoFormPropagatedHeight = '';

  constructor() {
    this.ssoConfiguration = this.loginService.getSsoConfiguration()
    this.useHassoForm = this.ssoConfiguration.usage?.toUpperCase().startsWith('OAUTHHASSO');

    this.route?.queryParams?.subscribe((params) => {
      if (params?.prefillUsername) {
        this.prefillUsername = params.prefillUsername;
      }
      if(params?.clientNo) {
        this.assignClientNo = params.clientNo
      }
      if (params?.returnUrl) {
        this.returnUrl = params.returnUrl;
        this.handleRedirect();
      }
    });

    this.authSub = this.loginService?.getAuthData()?.subscribe((authData) => {
      this.authData = authData;
      this.isAuthenticated = !!authData;
      if(this.isAuthenticated && this.assignClientNo > 0) {
        this.redirecting = true;
        this.loginService.assignClientNo(this.assignClientNo).subscribe({
          next: res=> this.handleRedirect(),
          error: err => this.handleRedirect()
        });
      } else {
        this.handleRedirect();
      }
    });

    if (this.useHassoForm && isPlatformBrowser(this.platformId)) {
      window.addEventListener("message", (event) => {
          this.loading = false;
          const eventData = typeof event.data == 'string' ? JSON.parse(event.data) : event.data;

          if (eventData.eventNature == 'ssoAjaxViewLoaded') {
            this.ssoFormPropagatedHeight = eventData.formHeight ? (eventData.formHeight + 5) + 'px' : 'inherit';
          } else {
            const alreadyLoggedIn = eventData.code === 2000;
            const justLoggedIn = typeof eventData.code == 'string' && eventData.code.match(/^[0-9a-f]{16,}$/);
            if (alreadyLoggedIn || justLoggedIn) {
              this.loginService.checkStatus();
            }
          }
        },
        false,
      );
    }
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder?.group({
      email: [null, [Validators.required, Validators.email]],
      password: [null, Validators.required]
    });

    if (this.useHassoForm && isPlatformBrowser(this.platformId)) {
      this.loading = true;

      let returnUri = this.ssoConfiguration.returnUrl;
      if (!!this.navigationService.getPreviousUrl()) {
        returnUri = location.origin + this.navigationService.getPreviousUrl();
      }

      let loginBoxParams = {
        client_id: this.ssoConfiguration.clientId,
        redirect_uri: encodeURIComponent(returnUri),
        responsetype: 'event',
        domain: encodeURIComponent(location.origin),
        ajaxMode: 1,
        formId: 'Login',
      };
      if (this.prefillUsername.length) {
        let encodedUsername = encodeURIComponent(this.prefillUsername); // sanitize user input
        loginBoxParams['uSrNaMe'] = encodedUsername.replace(/%40/, '@'); // just convert the @ sign back
      }

      this.hassoLoginUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.ssoConfiguration.ssoHost + '/auth/authorize?' + new HttpParams({fromObject: loginBoxParams}).toString()
      );
    }
  }

  onLogin(): void {
    if (this.loginForm?.valid) {
      this.loading = true;
      this.loginService.login(this.loginForm.get('email').value, this.loginForm.get('password').value, this.returnUrl).subscribe((res) => {
        if (res && res.token !== 0 && res.status !== 'error') {
        } else {
          this.error = res.message;
          this.loading = false;
        }
      }, error => {
        this.error = error.error;
        this.loading = false;
      });
    }
  }

  private handleRedirect(): void {
    this.redirecting = false;
    if (this.isAuthenticated && this.returnUrl) {
      this.router
        .navigateByUrl(this.returnUrl, {replaceUrl: true})
        .then(() => null);
    }
  }

  onBack(): void {
    history.back();
  }

}
